import {
  Checkbox,
  ComboboxItem,
  Group,
  MultiSelect,
  NativeSelect,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  capitalize,
  formatCoding,
  HTTP_HL7_ORG,
  getExtension,
  TypedValue,
  typedValueToString,
} from '@medplum/core';
import {
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
  QuestionnaireItemInitial,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import { ChangeEvent, useContext } from 'react';
import { AttachmentInput } from '@medplum-components/AttachmentInput/AttachmentInput';
import { QuestionnaireFormContext } from '../QuestionnaireForm.context';
import {
  CheckboxFormSection,
  DateTimeInput,
  getItemAnswerOptionValue,
  getItemInitialValue,
  getNewMultiSelectValues,
  getQuestionnaireItemReferenceFilter,
  getQuestionnaireItemReferenceTargetTypes,
  QuantityInput,
  QuestionnaireItemType,
  ReferenceInput,
} from '@medplum/react';
import { SignatureInput } from '../source-offset/SignatureInput';
import { QuestionnaireChoiceRadioInput } from '../source-offset/QuestionnaireChoiceRadioInput';
import { useTranslation } from 'react-i18next';
import { AutoCompleteInput } from '../source-offset/AutoCompleteInput';
import {
  HL7,
  QUESTIONNAIRE_API_EXT_EXTENSION_URL,
  QUESTIONNAIRE_API_URL_EXTENSION_URL,
  QUESTIONNAIRE_IS_MULTI_EXTENSION_URL,
  QUESTIONNAIRE_PREFIX_EXTENSION_URL,
  QUESTIONNAIRE_Q_DESC_EXTENSION_URL,
  QUESTIONNAIRE_Q_PLACEHOLDER_EXTENSION_URL,
  QUESTIONNAIRE_SYSTEM_EXTENSION_URL,
} from '@consts/systems.consts';
import { CodingInput } from '../../CodingInput/CodingInput';

export interface QuestionnaireFormItemProps {
  readonly item: QuestionnaireItem;
  readonly index: number;
  readonly required?: boolean;
  readonly response: QuestionnaireResponseItem;
  readonly onChange: (newResponseItem: QuestionnaireResponseItem) => void;
}

export const QuestionnaireFormItem = (
  props: QuestionnaireFormItemProps
): JSX.Element | null => {
  const context = useContext(QuestionnaireFormContext);
  const item = props.item;
  const response = props.response;

  //----------------------------------------------------------------------------
  /**
   * extract info about if and how we want to add a description to a question
   * the initial goal was to add a faded description
   * rigth now works only for type 'string'
   */
  const descriptionExt =
    item.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_Q_DESC_EXTENSION_URL
    )?.valueString || '';

  //----------------------------------------------------------------------------
  /**
   * extract info about if and how we want to add a placeholder to a question
   */
  const placeholderExt =
    item.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_Q_PLACEHOLDER_EXTENSION_URL
    )?.valueString || '';

  //----------------------------------------------------------------------------
  /**
   * extract unit
   */
  const unitExt =
    item.extension?.find(
      (ext) => ext.url === HL7.ORG.SD.QUESTIONNAIRE_ITEM_UNIT
    )?.valueQuantity?.unit || '';

  //----------------------------------------------------------------------------
  function onChangeAnswer(
    newResponseAnswer:
      | QuestionnaireResponseItemAnswer
      | QuestionnaireResponseItemAnswer[]
  ): void {
    let updatedAnswers: QuestionnaireResponseItemAnswer[];
    if (Array.isArray(newResponseAnswer)) {
      // It's a multi-select case, so use the array directly.
      updatedAnswers = newResponseAnswer;
    } else if (props.index >= (props.response?.answer?.length ?? 0)) {
      // if adding a new answer
      updatedAnswers = (props.response?.answer ?? []).concat([
        newResponseAnswer,
      ]);
    } else {
      // if updating an existing answer
      const newAnswers = (props.response?.answer ?? []).map((a, idx) =>
        idx === props.index ? newResponseAnswer : a
      ) as QuestionnaireResponseItemAnswer[];
      updatedAnswers = newAnswers ?? [];
    }
    props.onChange({
      id: response?.id,
      linkId: response?.linkId,
      text: item.text,
      answer: updatedAnswers,
    });
  }

  const type = item.type;
  if (!type) {
    return null;
  }

  const name = item.linkId;
  if (!name) {
    return null;
  }

  const initial =
    item.initial && item.initial.length > 0 ? item.initial[0] : undefined;
  const defaultValue =
    getCurrentAnswer(response, props.index) ?? getItemInitialValue(initial);
  const validationError = getExtension(
    response,
    `${HTTP_HL7_ORG}/fhir/StructureDefinition/questionnaire-validationError`
  );

  let formComponent: JSX.Element | null = null;

  switch (type) {
    case QuestionnaireItemType.display:
      formComponent = <p key={props.item.linkId}>{props.item.text}</p>;
      break;
    case QuestionnaireItemType.boolean:
      formComponent = (
        <CheckboxFormSection
          key={props.item.linkId}
          title={props.item.text}
          htmlFor={props.item.linkId}
        >
          <Checkbox
            id={props.item.linkId}
            name={props.item.linkId}
            defaultChecked={defaultValue?.value}
            onChange={(e) =>
              onChangeAnswer({ valueBoolean: e.currentTarget.checked })
            }
          />
        </CheckboxFormSection>
      );
      break;
    case QuestionnaireItemType.decimal:
      formComponent = (
        <TextInput
          type="number"
          step="any"
          id={name}
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          placeholder={placeholderExt}
          onChange={(e) =>
            onChangeAnswer({
              valueDecimal:
                e.currentTarget.value === ''
                  ? undefined
                  : e.currentTarget.valueAsNumber,
            })
          }
        />
      );
      break;
    case QuestionnaireItemType.integer:
      formComponent = (
        <TextInput
          type="number"
          step={1}
          id={name}
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          placeholder={placeholderExt}
          rightSection={
            <Text size="sm" c="dimmed">
              {unitExt}
            </Text>
          }
          onChange={(e) =>
            onChangeAnswer({
              valueInteger:
                e.currentTarget.value === ''
                  ? undefined
                  : e.currentTarget.valueAsNumber,
            })
          }
        />
      );
      break;
    case QuestionnaireItemType.date: {
      const today = new Date().toISOString().split('T')[0];
      const defaultToday = item.initial?.[0].valueString === 'today';
      formComponent = (
        <TextInput
          type="date"
          id={name}
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultToday ? today : defaultValue?.value}
          onChange={(e) => onChangeAnswer({ valueDate: e.currentTarget.value })}
        />
      );
      break;
    }
    case QuestionnaireItemType.dateTime:
      formComponent = (
        <DateTimeInput
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          onChange={(newValue: string) =>
            onChangeAnswer({ valueDateTime: newValue })
          }
        />
      );
      break;
    case QuestionnaireItemType.time:
      formComponent = (
        <TextInput
          type="time"
          id={name}
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          onChange={(e) => onChangeAnswer({ valueTime: e.currentTarget.value })}
        />
      );
      break;
    case QuestionnaireItemType.string:
    case QuestionnaireItemType.url:
      formComponent = (
        <TextInput
          id={name}
          name={name}
          description={descriptionExt}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          placeholder={placeholderExt}
          rightSection={
            <Text size="sm" c="dimmed" mr={'xl'}>
              {unitExt}
            </Text>
          }
          onChange={(e) => {
            const value = e.currentTarget.value;
            onChangeAnswer({ valueString: value === '' ? undefined : value });
          }}
        />
      );
      break;
    case QuestionnaireItemType.text:
      formComponent = (
        <Textarea
          id={name}
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          onChange={(e) => {
            const value = e.currentTarget.value;
            onChangeAnswer({ valueString: value === '' ? undefined : value });
          }}
          autosize
          minRows={2}
          maxRows={6}
        />
      );
      break;
    case QuestionnaireItemType.attachment:
      formComponent = (
        <Group py={4}>
          {name.includes('-signature') ? (
            <SignatureInput
              path=""
              name={name}
              defaultValue={defaultValue?.value}
              onChange={(newValue) =>
                onChangeAnswer({ valueAttachment: newValue })
              }
            />
          ) : (
            <AttachmentInput
              path=""
              name={name}
              defaultValue={defaultValue?.value}
              onChange={(newValue) =>
                onChangeAnswer({ valueAttachment: newValue })
              }
            />
          )}
        </Group>
      );
      break;
    case QuestionnaireItemType.reference:
      formComponent = (
        <ReferenceInput
          name={name}
          required={props.required ?? item.required}
          targetTypes={getQuestionnaireItemReferenceTargetTypes(item)}
          searchCriteria={getQuestionnaireItemReferenceFilter(
            item,
            context.subject,
            context.encounter
          )}
          defaultValue={defaultValue?.value}
          onChange={(newValue) => onChangeAnswer({ valueReference: newValue })}
        />
      );
      break;
    case QuestionnaireItemType.quantity:
      formComponent = (
        <QuantityInput
          path=""
          name={name}
          required={props.required ?? item.required}
          defaultValue={defaultValue?.value}
          onChange={(newValue) => onChangeAnswer({ valueQuantity: newValue })}
          disableWheel
        />
      );
      break;
    case QuestionnaireItemType.choice:
    case QuestionnaireItemType.openChoice:
      if (isDropDownChoice(item) && !item.answerValueSet) {
        formComponent = (
          <QuestionnaireChoiceDropDownInput
            name={name}
            item={item}
            initial={initial}
            response={response}
            onChangeAnswer={(e) => onChangeAnswer(e)}
          />
        );
      } else if (isMultiSelectChoice(item) && !item.answerValueSet) {
        formComponent = (
          <QuestionnaireMultiSelectInput
            name={name}
            item={item}
            initial={initial}
            response={response}
            onChangeAnswer={(e) => onChangeAnswer(e)}
          />
        );
      } else {
        formComponent = (
          <QuestionnaireChoiceSetInput
            name={name}
            item={item}
            initial={initial}
            response={response}
            onChangeAnswer={(e) => onChangeAnswer(e)}
          />
        );
      }
      break;
    default:
      return null;
  }

  return (
    <>
      {formComponent}
      {validationError?.valueString && (
        <Text c="red" size="lg" mt={4}>
          {validationError.valueString}
        </Text>
      )}
    </>
  );
};

export interface QuestionnaireChoiceInputProps {
  readonly name: string;
  readonly item: QuestionnaireItem;
  readonly initial: QuestionnaireItemInitial | undefined;
  readonly response: QuestionnaireResponseItem;
  readonly onChangeAnswer: (
    newResponseAnswer:
      | QuestionnaireResponseItemAnswer
      | QuestionnaireResponseItemAnswer[]
  ) => void;
}

function QuestionnaireChoiceDropDownInput(
  props: QuestionnaireChoiceInputProps
): JSX.Element {
  const { name, item, initial, response } = props;
  const { t } = useTranslation(['medplum-react']);

  if (!item.answerOption?.length) {
    return <NoAnswerDisplay />;
  }

  const initialValue = getItemInitialValue(initial);

  const data = [''];

  for (const option of item.answerOption) {
    const optionValue = getItemAnswerOptionValue(option);
    data.push(typedValueToString(optionValue) as string);
  }

  const defaultValue = getCurrentAnswer(response) ?? initialValue;

  if (item.repeats) {
    const { propertyName, data } = formatSelectData(props.item);
    const currentAnswer = getCurrentMultiSelectAnswer(response);

    return (
      <MultiSelect
        data={data}
        placeholder={t('questionnaireForm.labels.selectItems', 'Select items', {
          ns: 'medplum-react',
        })}
        searchable
        defaultValue={currentAnswer || [typedValueToString(initialValue)]}
        onChange={(selected) => {
          const values = getNewMultiSelectValues(selected, propertyName, item);
          props.onChangeAnswer(values);
        }}
      />
    );
  }

  return (
    <NativeSelect
      id={name}
      name={name}
      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
        const index = e.currentTarget.selectedIndex;
        if (index === 0) {
          props.onChangeAnswer({});
          return;
        }
        const option = (item.answerOption as QuestionnaireItemAnswerOption[])[
          index - 1
        ];
        const optionValue = getItemAnswerOptionValue(option);
        const propertyName = 'value' + capitalize(optionValue.type);
        props.onChangeAnswer({ [propertyName]: optionValue.value });
      }}
      defaultValue={formatCoding(defaultValue?.value) || defaultValue?.value}
      data={data}
    />
  );
}

function QuestionnaireMultiSelectInput(
  props: QuestionnaireChoiceInputProps
): JSX.Element {
  const { item, initial, response } = props;
  const { t } = useTranslation(['medplum-react']);

  if (!item.answerOption?.length) {
    return <NoAnswerDisplay />;
  }

  const initialValue = getItemInitialValue(initial);
  const { propertyName, data } = formatSelectData(props.item);
  const currentAnswer = getCurrentMultiSelectAnswer(response);

  return (
    <MultiSelect
      data={data}
      placeholder={t('questionnaireForm.labels.selectItems', 'Select items', {
        ns: 'medplum-react',
      })}
      searchable
      defaultValue={currentAnswer || [typedValueToString(initialValue)]}
      onChange={(selected) => {
        const values = getNewMultiSelectValues(selected, propertyName, item);
        props.onChangeAnswer(values);
      }}
    />
  );
}

function QuestionnaireChoiceSetInput(
  props: QuestionnaireChoiceInputProps
): JSX.Element {
  const { name, item, initial, onChangeAnswer, response } = props;

  //----------------------------------------------------------------------------
  const apiExt = item.extension?.some(
    (e) =>
      e.url === QUESTIONNAIRE_API_EXT_EXTENSION_URL && e.valueBoolean === true
  );

  //----------------------------------------------------------------------------
  if (!item.answerOption?.length && !item.answerValueSet && !apiExt) {
    return <NoAnswerDisplay />;
  }

  if (item.answerValueSet) {
    return (
      <CodingInput
        path=""
        name={name}
        binding={item.answerValueSet}
        response={response}
        // defaultValue={defaultValue?.value}
        onChange={(code) => onChangeAnswer({ valueCoding: code })}
        creatable={item.type === QuestionnaireItemType.openChoice}
      />
    );
  }

  if (apiExt) {
    const apiUrl = item.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_API_URL_EXTENSION_URL
    )?.valueString;

    const system = item.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_SYSTEM_EXTENSION_URL
    )?.valueString;

    const prefix = item.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_PREFIX_EXTENSION_URL
    )?.valueString;

    const isMulti = item.extension?.find(
      (ext) => ext.url === QUESTIONNAIRE_IS_MULTI_EXTENSION_URL
    )?.valueBoolean;

    const defaultAnswers =
      (response?.answer?.length ?? 0) > 0
        ? response.answer?.map((ans: any) => ans.valueCoding).filter(Boolean)
        : getItemInitialValue(initial)?.value;

    return (
      <AutoCompleteInput
        apiurl={apiUrl}
        system={system}
        prefix={prefix}
        isMulti={isMulti}
        value={defaultAnswers}
        onChange={(coding) => {
          if (Array.isArray(coding)) {
            onChangeAnswer(
              coding.length > 0 ? coding.map((c) => ({ valueCoding: c })) : []
            );
          } else {
            onChangeAnswer(coding ? { valueCoding: coding } : {});
          }
        }}
      />
    );
  }

  return (
    <QuestionnaireChoiceRadioInput
      name={response?.id ?? name}
      item={item}
      initial={initial}
      response={response}
      onChangeAnswer={onChangeAnswer}
    />
  );
}

function NoAnswerDisplay(): JSX.Element {
  return <TextInput disabled placeholder="No Answers Defined" />;
}

// eslint-disable-next-line react-refresh/only-export-components
export function getCurrentAnswer(
  response: QuestionnaireResponseItem,
  index: number = 0
): TypedValue {
  const results = response.answer;
  return getItemAnswerOptionValue(results?.[index] ?? {});
}

function getCurrentMultiSelectAnswer(
  response: QuestionnaireResponseItem
): string[] {
  const results = response.answer;
  if (!results) {
    return [];
  }
  const typedValues = results.map((a) => getItemAnswerOptionValue(a));
  return typedValues.map((type) => formatCoding(type?.value) || type?.value);
}

function isDropDownChoice(item: QuestionnaireItem): boolean {
  return !!item.extension?.some(
    (e) =>
      e.url === HL7.ORG.SD.QUESTIONNAIRE_ITEM_CONTROL &&
      e.valueCodeableConcept?.coding?.[0]?.code === 'drop-down'
  );
}

function isMultiSelectChoice(item: QuestionnaireItem): boolean {
  return !!item.extension?.some(
    (e) =>
      e.url === HL7.ORG.SD.QUESTIONNAIRE_ITEM_CONTROL &&
      e.valueCodeableConcept?.coding?.[0]?.code === 'multi-select'
  );
}

interface FormattedData {
  readonly propertyName: string;
  readonly data: ComboboxItem[];
}

function formatSelectData(item: QuestionnaireItem): FormattedData {
  if (item.answerOption?.length === 0) {
    return { propertyName: '', data: [] };
  }
  const option = (item.answerOption as QuestionnaireItemAnswerOption[])[0];
  const optionValue = getItemAnswerOptionValue(option);
  const propertyName = 'value' + capitalize(optionValue.type);

  const data = (item.answerOption ?? []).map((answerOption) => {
    const answerOptionValue = getItemAnswerOptionValue(answerOption);
    const answerOptionValueStr = typedValueToString(answerOptionValue);
    return {
      value: answerOptionValueStr,
      label: answerOptionValueStr,
    };
  });
  return { propertyName, data };
}
